import { useState, useEffect } from 'react';
import axios from 'axios';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend, ReferenceLine } from 'recharts';
import { TrendingUp, User, Filter } from 'lucide-react';
import { normalizeNote, moyenneNormalisee } from '../utils/normalizeNote';

const EvolutionNotesChart = ({ formateurId, stagiaires = [] }) => {
  const [selectedStagiaire, setSelectedStagiaire] = useState('');
  const [selectedCategorie, setSelectedCategorie] = useState('all');
  const [notes, setNotes] = useState([]);
  const [loading, setLoading] = useState(false);

  const categories = [
    'Briefing', 'Encadrement', 'Sécurité', 'Pédagogie', 
    'Procédure d\'intervention', 'Animation',
    'Compétences Générales', 'Prérequis', 'Épreuves Formatives'
  ];

  // Couleurs pour les catégories
  const categoryColors = {
    'Briefing': '#3b82f6',
    'Encadrement': '#22c55e',
    'Sécurité': '#ef4444',
    'Pédagogie': '#f59e0b',
    'Aisance': '#0ea5e9',
    'Procédure d\'intervention': '#14b8a6',
    'Animation': '#f97316',
    'Compétences Générales': '#8b5cf6',
    'Prérequis': '#06b6d4',
    'Épreuves Formatives': '#ec4899'
  };

  useEffect(() => {
    if (selectedStagiaire) {
      loadNotes();
    }
  }, [selectedStagiaire, selectedCategorie]);

  const loadNotes = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`/notes/stagiaire/${selectedStagiaire}`);
      setNotes(response.data || []);
    } catch (error) {
      setNotes([]);
    }
    setLoading(false);
  };

  // Préparer les données pour le graphique
  const prepareChartData = () => {
    if (!notes.length) return [];

    // Filtrer par catégorie si nécessaire
    const filteredNotes = selectedCategorie === 'all' 
      ? notes 
      : notes.filter(n => n.categorie === selectedCategorie);

    // Grouper par date et catégorie, en gardant le détail des items
    const groupedByDate = {};
    filteredNotes.forEach(note => {
      const date = note.date ? note.date.split('T')[0] : 'Sans date';
      if (!groupedByDate[date]) {
        groupedByDate[date] = { date, _details: [] };
      }
      const cat = note.categorie || 'Autre';
      if (!groupedByDate[date][cat]) {
        groupedByDate[date][cat] = { total: 0, count: 0 };
      }
      groupedByDate[date][cat].total += normalizeNote(note);
      groupedByDate[date][cat].count += 1;
      // Stocker le détail (item/support + commentaire) pour le tooltip
      groupedByDate[date]._details.push({
        categorie: cat,
        item: note.item || '',
        note: normalizeNote(note),
        commentaire: note.commentaire || ''
      });
    });

    // Calculer les moyennes et trier par date
    return Object.values(groupedByDate)
      .map(entry => {
        const result = { date: entry.date, _details: entry._details || [] };
        Object.keys(entry).forEach(key => {
          if (key !== 'date' && key !== '_details' && entry[key].count > 0) {
            result[key] = Math.round((entry[key].total / entry[key].count) * 10) / 10;
          }
        });
        return result;
      })
      .sort((a, b) => new Date(a.date) - new Date(b.date));
  };

  const chartData = prepareChartData();
  
  // Déterminer les catégories présentes dans les données
  const presentCategories = [...new Set(notes.map(n => n.categorie).filter(Boolean))];

  // Calculer les stats
  const calculateStats = () => {
    if (!notes.length) return { moyenne: 0, evolution: 0, total: 0 };
    
    const filteredNotes = selectedCategorie === 'all' 
      ? notes 
      : notes.filter(n => n.categorie === selectedCategorie);
    
    const moyenne = moyenneNormalisee(filteredNotes);
    
    // Calculer l'évolution (dernière note - première note)
    const sortedNotes = [...filteredNotes].sort((a, b) => new Date(a.date) - new Date(b.date));
    const firstNote = normalizeNote(sortedNotes[0] || 0);
    const lastNote = normalizeNote(sortedNotes[sortedNotes.length - 1] || 0);
    const evolution = lastNote - firstNote;
    
    return {
      moyenne: Math.round(moyenne * 10) / 10,
      evolution: Math.round(evolution * 10) / 10,
      total: filteredNotes.length
    };
  };

  const stats = calculateStats();

  const formatDate = (dateStr) => {
    if (!dateStr || dateStr === 'Sans date') return dateStr;
    const date = new Date(dateStr);
    return date.toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit' });
  };

  return (
    <div className="bg-white border border-slate-200 rounded-lg p-6 shadow-sm" data-testid="evolution-notes-chart">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <TrendingUp className="h-5 w-5 text-[#8DC63F]" />
          <h2 className="text-xl font-display font-semibold text-[#0F172A]">Évolution des Notes</h2>
        </div>
      </div>

      {/* Filtres */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
        <div className="space-y-1">
          <label className="text-sm font-medium text-slate-700 flex items-center gap-1">
            <User className="h-4 w-4" /> Stagiaire
          </label>
          <select
            value={selectedStagiaire}
            onChange={(e) => setSelectedStagiaire(e.target.value)}
            className="w-full h-10 px-3 py-2 text-sm border border-slate-200 rounded-md focus:outline-none focus:ring-2 focus:ring-[#8DC63F]"
            data-testid="select-stagiaire-evolution"
          >
            <option value="">Sélectionner un stagiaire</option>
            {stagiaires.map(s => (
              <option key={s.user_id} value={s.user_id}>
                {s.prenom} {s.nom}
              </option>
            ))}
          </select>
        </div>

        <div className="space-y-1">
          <label className="text-sm font-medium text-slate-700 flex items-center gap-1">
            <Filter className="h-4 w-4" /> Catégorie
          </label>
          <select
            value={selectedCategorie}
            onChange={(e) => setSelectedCategorie(e.target.value)}
            className="w-full h-10 px-3 py-2 text-sm border border-slate-200 rounded-md focus:outline-none focus:ring-2 focus:ring-[#8DC63F]"
            data-testid="select-categorie-evolution"
          >
            <option value="all">Toutes les catégories</option>
            {categories.map(cat => (
              <option key={cat} value={cat}>{cat}</option>
            ))}
          </select>
        </div>
      </div>

      {/* Stats rapides */}
      {selectedStagiaire && notes.length > 0 && (
        <div className="grid grid-cols-3 gap-3 mb-4">
          <div className="text-center p-3 bg-slate-50 rounded-lg border border-slate-200">
            <p className="text-2xl font-bold text-[#0F172A]">{stats.total}</p>
            <p className="text-xs text-slate-600">Notes</p>
          </div>
          <div className="text-center p-3 bg-blue-50 rounded-lg border border-blue-200">
            <p className="text-2xl font-bold text-blue-600">{stats.moyenne}/10</p>
            <p className="text-xs text-blue-700">Moyenne</p>
          </div>
          <div className={`text-center p-3 rounded-lg border ${stats.evolution >= 0 ? 'bg-green-50 border-green-200' : 'bg-red-50 border-red-200'}`}>
            <p className={`text-2xl font-bold ${stats.evolution >= 0 ? 'text-green-600' : 'text-red-600'}`}>
              {stats.evolution >= 0 ? '+' : ''}{stats.evolution}
            </p>
            <p className={`text-xs ${stats.evolution >= 0 ? 'text-green-700' : 'text-red-700'}`}>Évolution</p>
          </div>
        </div>
      )}

      {/* Graphique */}
      {!selectedStagiaire ? (
        <div className="h-64 flex items-center justify-center text-slate-400">
          <p>Sélectionnez un stagiaire pour voir l'évolution de ses notes</p>
        </div>
      ) : loading ? (
        <div className="h-64 flex items-center justify-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#8DC63F]"></div>
        </div>
      ) : chartData.length === 0 ? (
        <div className="h-64 flex items-center justify-center text-slate-400">
          <p>Aucune note disponible pour ce stagiaire</p>
        </div>
      ) : (
        <ResponsiveContainer width="100%" height={300}>
          <LineChart data={chartData} margin={{ top: 5, right: 30, left: 20, bottom: 5 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
            <XAxis 
              dataKey="date" 
              tickFormatter={formatDate}
              tick={{ fontSize: 11, fill: '#64748b' }}
            />
            <YAxis 
              domain={[0, 10]} 
              tick={{ fontSize: 11, fill: '#64748b' }}
              tickCount={6}
            />
            <Tooltip 
              content={({ active, payload, label }) => {
                if (!active || !payload?.length) return null;
                const details = payload[0]?.payload?._details || [];
                return (
                  <div className="bg-white border border-slate-200 rounded-lg shadow-lg p-3 max-w-xs">
                    <p className="font-semibold text-sm text-slate-800 mb-2">{formatDate(label)}</p>
                    {details.map((d, i) => (
                      <div key={i} className="mb-1.5 last:mb-0">
                        <div className="flex items-center justify-between gap-3">
                          <span className="text-xs text-slate-600">
                            <span className="font-medium" style={{ color: categoryColors[d.categorie] || '#8DC63F' }}>{d.categorie}</span>
                            {d.item && <span className="text-slate-400"> / {d.item}</span>}
                          </span>
                          <span className="text-xs font-bold text-slate-800">{d.note.toFixed(0)}/10</span>
                        </div>
                        {d.commentaire && (
                          <p className="text-[10px] text-slate-500 mt-0.5 leading-tight truncate">{d.commentaire.substring(0, 80)}{d.commentaire.length > 80 ? '...' : ''}</p>
                        )}
                      </div>
                    ))}
                  </div>
                );
              }}
            />
            <ReferenceLine y={5} stroke="#f59e0b" strokeDasharray="5 5" label={{ value: 'Seuil', position: 'right', fontSize: 10 }} />
            <Legend />
            
            {/* Lignes pour chaque catégorie présente */}
            {selectedCategorie === 'all' ? (
              presentCategories.map(cat => (
                <Line
                  key={cat}
                  type="monotone"
                  dataKey={cat}
                  name={cat}
                  stroke={categoryColors[cat] || '#8DC63F'}
                  strokeWidth={2}
                  dot={{ r: 4 }}
                  activeDot={{ r: 6 }}
                  connectNulls
                />
              ))
            ) : (
              <Line
                type="monotone"
                dataKey={selectedCategorie}
                name={selectedCategorie}
                stroke={categoryColors[selectedCategorie] || '#8DC63F'}
                strokeWidth={2}
                dot={{ r: 4 }}
                activeDot={{ r: 6 }}
                connectNulls
              />
            )}
          </LineChart>
        </ResponsiveContainer>
      )}
    </div>
  );
};

export default EvolutionNotesChart;
